/**
 * Hand-verified origins - spec §11 point 4, "transit-station-first".
 *
 * Covers the case that motivated a custom origin at all: someone creating a
 * link from the office for a meeting they will travel to from home, possibly
 * in another town. A list of central stations alone would not have helped.
 *
 * v1 is Vienna and its commuter belt, because that is the only city whose
 * Google coverage has been verified by hand (§16). Adding a city means
 * verifying it, not appending coordinates.
 */

export interface Landmark {
  name: string
  lat: number
  lng: number
  kind: 'station' | 'district' | 'town'
  /** Alternate spellings people actually type. */
  aka?: readonly string[]
}

export const LANDMARKS: readonly Landmark[] = [
  // ── major interchanges ────────────────────────────────────────────────
  { name: 'Hauptbahnhof', lat: 48.1852, lng: 16.3757, kind: 'station', aka: ['hbf', 'main station'] },
  { name: 'Westbahnhof', lat: 48.1965, lng: 16.3378, kind: 'station' },
  { name: 'Praterstern', lat: 48.2184, lng: 16.3919, kind: 'station' },
  { name: 'Karlsplatz', lat: 48.2004, lng: 16.3696, kind: 'station' },
  { name: 'Stephansplatz', lat: 48.2082, lng: 16.3738, kind: 'station', aka: ['stephansdom', 'city'] },
  { name: 'Schottentor', lat: 48.2153, lng: 16.3625, kind: 'station' },
  { name: 'Landstraße / Wien Mitte', lat: 48.2065, lng: 16.385, kind: 'station', aka: ['wien mitte'] },
  { name: 'Schwedenplatz', lat: 48.2117, lng: 16.3775, kind: 'station' },
  { name: 'Meidling', lat: 48.1747, lng: 16.3325, kind: 'station', aka: ['philadelphiabrücke'] },
  { name: 'Floridsdorf', lat: 48.2566, lng: 16.4004, kind: 'station' },
  { name: 'Hütteldorf', lat: 48.1938, lng: 16.2622, kind: 'station' },
  { name: 'Heiligenstadt', lat: 48.2489, lng: 16.3661, kind: 'station' },
  { name: 'Simmering', lat: 48.1717, lng: 16.4204, kind: 'station' },
  { name: 'Kagran', lat: 48.2494, lng: 16.4406, kind: 'station', aka: ['donauzentrum'] },
  { name: 'Ottakring', lat: 48.211, lng: 16.3105, kind: 'station' },
  { name: 'Reumannplatz', lat: 48.174, lng: 16.3782, kind: 'station', aka: ['favoriten'] },
  { name: 'Spittelau', lat: 48.2364, lng: 16.3577, kind: 'station' },
  { name: 'Längenfeldgasse', lat: 48.1846, lng: 16.3362, kind: 'station' },
  { name: 'Josefstädter Straße', lat: 48.2124, lng: 16.3384, kind: 'station' },
  { name: 'Aspern Nord', lat: 48.2318, lng: 16.4986, kind: 'station', aka: ['seestadt'] },

  // ── districts, for people who think in neighbourhoods ─────────────────
  { name: 'Neubau (7.)', lat: 48.2019, lng: 16.3487, kind: 'district', aka: ['neubaugasse', '1070'] },
  { name: 'Leopoldstadt (2.)', lat: 48.2166, lng: 16.3861, kind: 'district', aka: ['1020'] },
  { name: 'Wieden (4.)', lat: 48.1926, lng: 16.3671, kind: 'district', aka: ['1040', 'naschmarkt'] },
  { name: 'Margareten (5.)', lat: 48.1875, lng: 16.3576, kind: 'district', aka: ['1050'] },
  { name: 'Alsergrund (9.)', lat: 48.2251, lng: 16.3577, kind: 'district', aka: ['1090'] },
  { name: 'Hietzing (13.)', lat: 48.1855, lng: 16.296, kind: 'district', aka: ['1130', 'schönbrunn'] },
  { name: 'Döbling (19.)', lat: 48.2478, lng: 16.3369, kind: 'district', aka: ['1190', 'grinzing'] },
  { name: 'Brigittenau (20.)', lat: 48.2419, lng: 16.3806, kind: 'district', aka: ['1200'] },
  { name: 'Donaustadt (22.)', lat: 48.2333, lng: 16.4667, kind: 'district', aka: ['1220'] },
  { name: 'Liesing (23.)', lat: 48.1391, lng: 16.2894, kind: 'district', aka: ['1230'] },

  // ── the commuter belt - the "I am two hours away" case ────────────────
  { name: 'Klosterneuburg', lat: 48.3053, lng: 16.3253, kind: 'town' },
  { name: 'Mödling', lat: 48.0857, lng: 16.2839, kind: 'town' },
  { name: 'Baden bei Wien', lat: 47.9956, lng: 16.2318, kind: 'town', aka: ['baden'] },
  { name: 'Schwechat', lat: 48.1408, lng: 16.4753, kind: 'town', aka: ['airport', 'flughafen'] },
  { name: 'Wiener Neustadt', lat: 47.8149, lng: 16.2497, kind: 'town' },
  { name: 'Stockerau', lat: 48.3847, lng: 16.2136, kind: 'town' },
  { name: 'Tulln', lat: 48.3306, lng: 16.0589, kind: 'town' },
  { name: 'Korneuburg', lat: 48.3453, lng: 16.3336, kind: 'town' },
  { name: 'Sankt Pölten', lat: 48.2047, lng: 15.6256, kind: 'town', aka: ['st pölten', 'st polten'] },
  { name: 'Bratislava', lat: 48.1486, lng: 17.1077, kind: 'town' },
]

/** Fold German umlauts so "Modling" finds "Mödling". */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    // Escapes, not literal characters: a combining-mark range is invisible in
    // source and any tool that re-normalises the file would silently break it.
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
}

export function searchLandmarks(query: string, limit = 6): Landmark[] {
  const q = normalize(query.trim())
  if (!q) return []

  const scored: { l: Landmark; score: number }[] = []

  for (const l of LANDMARKS) {
    const name = normalize(l.name)
    const hit =
      name.startsWith(q)
        ? 0 // prefix match ranks first
        : name.includes(q)
          ? 1
          : l.aka?.some((a) => normalize(a).includes(q))
            ? 2
            : -1

    if (hit >= 0) scored.push({ l, score: hit })
  }

  return scored
    .sort((a, b) => a.score - b.score || a.l.name.localeCompare(b.l.name))
    .slice(0, limit)
    .map((s) => s.l)
}
